import "core-js/modules/es.array.sort.js";
import { $constructor } from "./core.js";
import * as util from "./util.js";
const initializer = (inst, def) => {
  inst.name = "$ZodError";
  Object.defineProperty(inst, "_zod", {
    value: inst._zod,
    enumerable: false
  });
  Object.defineProperty(inst, "issues", {
    value: def,
    enumerable: false
  });
  inst.message = JSON.stringify(def, util.jsonStringifyReplacer, 2);
  Object.defineProperty(inst, "toString", {
    value: () => inst.message,
    enumerable: false
  });
};
export const $ZodError = $constructor("$ZodError", initializer);
export const $ZodRealError = $constructor("$ZodError", initializer, {
  Parent: Error
});
export function flattenError(error) {
  let mapper = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : issue => issue.message;
  const fieldErrors = {};
  const formErrors = [];
  for (const sub of error.issues) {
    if (sub.path.length > 0) {
      fieldErrors[sub.path[0]] = fieldErrors[sub.path[0]] || [];
      fieldErrors[sub.path[0]].push(mapper(sub));
    } else {
      formErrors.push(mapper(sub));
    }
  }
  return {
    formErrors,
    fieldErrors
  };
}
export function formatError(error) {
  let mapper = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : issue => issue.message;
  const fieldErrors = {
    _errors: []
  };
  const processError = error => {
    for (const issue of error.issues) {
      if (issue.code === "invalid_union" && issue.errors.length) {
        issue.errors.map(issues => processError({
          issues
        }));
      } else if (issue.code === "invalid_key") {
        processError({
          issues: issue.issues
        });
      } else if (issue.code === "invalid_element") {
        processError({
          issues: issue.issues
        });
      } else if (issue.path.length === 0) {
        fieldErrors._errors.push(mapper(issue));
      } else {
        let curr = fieldErrors;
        let i = 0;
        while (i < issue.path.length) {
          const el = issue.path[i];
          const terminal = i === issue.path.length - 1;
          if (!terminal) {
            curr[el] = curr[el] || {
              _errors: []
            };
          } else {
            curr[el] = curr[el] || {
              _errors: []
            };
            curr[el]._errors.push(mapper(issue));
          }
          curr = curr[el];
          i++;
        }
      }
    }
  };
  processError(error);
  return fieldErrors;
}
export function treeifyError(error) {
  let mapper = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : issue => issue.message;
  const result = {
    errors: []
  };
  const processError = function (error) {
    let path = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    var _a, _b;
    for (const issue of error.issues) {
      if (issue.code === "invalid_union" && issue.errors.length) {
        // regular union error
        issue.errors.map(issues => processError({
          issues
        }, issue.path));
      } else if (issue.code === "invalid_key") {
        processError({
          issues: issue.issues
        }, issue.path);
      } else if (issue.code === "invalid_element") {
        processError({
          issues: issue.issues
        }, issue.path);
      } else {
        const fullpath = [...path, ...issue.path];
        if (fullpath.length === 0) {
          result.errors.push(mapper(issue));
          continue;
        }
        let curr = result;
        let i = 0;
        while (i < fullpath.length) {
          const el = fullpath[i];
          const terminal = i === fullpath.length - 1;
          if (typeof el === "string") {
            var _curr$properties, _a$el;
            (_curr$properties = curr.properties) !== null && _curr$properties !== void 0 ? _curr$properties : curr.properties = {};
            (_a$el = (_a = curr.properties)[el]) !== null && _a$el !== void 0 ? _a$el : _a[el] = {
              errors: []
            };
            curr = curr.properties[el];
          } else {
            var _curr$items, _b$el;
            (_curr$items = curr.items) !== null && _curr$items !== void 0 ? _curr$items : curr.items = [];
            (_b$el = (_b = curr.items)[el]) !== null && _b$el !== void 0 ? _b$el : _b[el] = {
              errors: []
            };
            curr = curr.items[el];
          }
          if (terminal) {
            curr.errors.push(mapper(issue));
          }
          i++;
        }
      }
    }
  };
  processError(error);
  return result;
}
/** Format a ZodError as a human-readable string in the following form.
 *
 * From
 *
 * ```ts
 * ZodError {
 *   issues: [
 *     {
 *       expected: 'string',
 *       code: 'invalid_type',
 *       path: [ 'username' ],
 *       message: 'Invalid input: expected string'
 *     },
 *     {
 *       expected: 'number',
 *       code: 'invalid_type',
 *       path: [ 'favoriteNumbers', 1 ],
 *       message: 'Invalid input: expected number'
 *     }
 *   ];
 * }
 * ```
 *
 * to
 *
 * ```
 * username
 *   ✖ Expected number, received string at "username
 * favoriteNumbers[0]
 *   ✖ Invalid input: expected number
 * ```
 */
export function toDotPath(_path) {
  const segs = [];
  const path = _path.map(seg => typeof seg === "object" ? seg.key : seg);
  for (const seg of path) {
    if (typeof seg === "number") segs.push("[".concat(seg, "]"));else if (typeof seg === "symbol") segs.push("[".concat(JSON.stringify(String(seg)), "]"));else if (/[^\w$]/.test(seg)) segs.push("[".concat(JSON.stringify(seg), "]"));else {
      if (segs.length) segs.push(".");
      segs.push(seg);
    }
  }
  return segs.join("");
}
export function prettifyError(error) {
  const lines = [];
  // sort by path length
  const issues = [...error.issues].sort((a, b) => {
    var _a$path, _b$path;
    return ((_a$path = a.path) !== null && _a$path !== void 0 ? _a$path : []).length - ((_b$path = b.path) !== null && _b$path !== void 0 ? _b$path : []).length;
  });
  // Process each issue
  for (const issue of issues) {
    var _issue$path;
    lines.push("\u2716 ".concat(issue.message));
    if ((_issue$path = issue.path) !== null && _issue$path !== void 0 && _issue$path.length) lines.push("  \u2192 at ".concat(toDotPath(issue.path)));
  }
  // Convert Map to formatted string
  return lines.join("\n");
}